import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import { useAuth } from "../contexts/authContext";
import {
  Calendar,
  FileText,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  TrendingUp,
  AlertCircle,
  Plus,
  ArrowRight,
} from "lucide-react";

interface DashboardData {
  profile: {
    fullName: string;
    designation?: string;
    company: string;
    totalPaidLeaves: number;
    remainingPaidLeaves: number;
  };
  leaves: {
    pending: number;
    approved: number;
    rejected: number;
  };
  attendance: {
    totalRecords: number;
    currentMonth?: {
      workingDays: number;
      paidLeaves: number;
      unpaidLeaves: number;
    };
  };
  payslips: {
    total: number;
    approved: number;
    pending: number;
  };
}

export default function EmployeeDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      setError("");

      const token = localStorage.getItem("authToken");
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch all required data
      const [profileRes, leavesRes, attendanceRes, payslipsRes] =
        await Promise.all([
          axios.get(
            `${process.env.REACT_APP_BACKEND_URL}api/employees/profile/me`,
            { headers }
          ),
          axios.get(
            `${process.env.REACT_APP_BACKEND_URL}api/leaves/my-leaves`,
            { headers }
          ),
          axios.get(
            `${process.env.REACT_APP_BACKEND_URL}api/employees/me/attendance`,
            { headers }
          ),
          axios.get(
            `${process.env.REACT_APP_BACKEND_URL}api/employees/me/payslips`,
            { headers }
          ),
        ]);

      // Process leaves data
      const leaves = leavesRes.data.leaves || [];
      const leavesStats = {
        pending: leaves.filter((l: any) => l.status === "Pending").length,
        approved: leaves.filter((l: any) => l.status === "Approved").length,
        rejected: leaves.filter((l: any) => l.status === "Rejected").length,
      };

      // Process attendance data
      const attendanceRecords = attendanceRes.data || [];
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth() + 1;
      const currentYear = currentDate.getFullYear();
      const currentMonthAttendance = attendanceRecords.find(
        (a: any) => a.month === currentMonth && a.year === currentYear
      );

      // Process payslips data
      const payslips = payslipsRes.data || [];
      const payslipsStats = {
        total: payslips.length,
        approved: payslips.filter((p: any) => p.status === "approved").length,
        pending: payslips.filter((p: any) => p.status === "pending").length,
      };

      // Use totalPaidLeaves and remainingPaidLeaves from API
      const totalLeaves = profileRes.data.totalPaidLeaves || 0;
      const remainingLeaves = profileRes.data.remainingPaidLeaves || 0;

      setDashboardData({
        profile: {
          fullName: profileRes.data.fullName,
          designation: profileRes.data.designation,
          company: profileRes.data.company,
          totalPaidLeaves: totalLeaves,
          remainingPaidLeaves: remainingLeaves,
        },
        leaves: leavesStats,
        attendance: {
          totalRecords: attendanceRecords.length,
          currentMonth: currentMonthAttendance
            ? {
                workingDays: currentMonthAttendance.workingDays,
                paidLeaves: currentMonthAttendance.paidLeaves,
                unpaidLeaves: currentMonthAttendance.unpaidLeaves,
              }
            : undefined,
        },
        payslips: payslipsStats,
      });
    } catch (error: any) {
      console.error("Error fetching dashboard data:", error);
      setError("Failed to load dashboard data");
    } finally {
      setIsLoading(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-2">
          <Spinner variant="ellipsis" size={40} />
          <p className="text-sm text-muted-foreground">
            Loading your dashboard...
          </p>
        </div>
      </div>
    );
  }

  if (error || !dashboardData) {
    return (
      <div className="container mx-auto px-6 py-8">
        <div className="p-6 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-800 font-medium">
              {error || "Failed to load dashboard"}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const attendancePercentage = dashboardData.attendance.currentMonth
    ? (
        (dashboardData.attendance.currentMonth.workingDays /
          (dashboardData.attendance.currentMonth.workingDays +
            dashboardData.attendance.currentMonth.paidLeaves +
            dashboardData.attendance.currentMonth.unpaidLeaves)) *
        100
      ).toFixed(0)
    : null;

  return (
    <div className="container mx-auto px-6 py-8">
      {/* Welcome Section */}
      <div className="space-y-2 mb-8">
        <h1 className="text-3xl font-bold tracking-tight">
          {getGreeting()}, {dashboardData.profile.fullName.split(" ")[0]}! 👋
        </h1>
        <p className="text-muted-foreground">
          {dashboardData.profile.designation && (
            <span>{dashboardData.profile.designation} • </span>
          )}
          {dashboardData.profile.company}
        </p>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
        {/* Remaining Leaves */}
        <Card
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate("/employee/leaves")}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Remaining Leaves
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">
                  {dashboardData.profile.remainingPaidLeaves}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  of {dashboardData.profile.totalPaidLeaves} total
                </p>
              </div>
              <Calendar className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        {/* Attendance This Month */}
        <Card
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate("/employee/attendance")}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Attendance This Month
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">
                  {attendancePercentage ? `${attendancePercentage}%` : "N/A"}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {dashboardData.attendance.currentMonth
                    ? `${dashboardData.attendance.currentMonth.workingDays} working days`
                    : "Not recorded yet"}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        {/* Pending Leaves */}
        <Card
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate("/employee/leaves")}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pending Requests
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">
                  {dashboardData.leaves.pending}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Leave applications
                </p>
              </div>
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>

        {/* Payslips */}
        <Card
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate("/employee/payslips")}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Payslips
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">
                  {dashboardData.payslips.total}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {dashboardData.payslips.approved} approved
                </p>
              </div>
              <FileText className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Button
            variant="outline"
            className="h-auto py-4 justify-start"
            onClick={() => navigate("/employee/leaves/apply")}
          >
            <Plus className="mr-2 h-5 w-5" />
            <div className="text-left">
              <div className="font-medium">Apply Leave</div>
              <div className="text-xs text-muted-foreground">
                Request time off
              </div>
            </div>
          </Button>

          <Button
            variant="outline"
            className="h-auto py-4 justify-start"
            onClick={() => navigate("/employee/salary")}
          >
            <DollarSign className="mr-2 h-5 w-5" />
            <div className="text-left">
              <div className="font-medium">View Salary</div>
              <div className="text-xs text-muted-foreground">
                Check salary details
              </div>
            </div>
          </Button>

          <Button
            variant="outline"
            className="h-auto py-4 justify-start"
            onClick={() => navigate("/employee/documents")}
          >
            <FileText className="mr-2 h-5 w-5" />
            <div className="text-left">
              <div className="font-medium">My Documents</div>
              <div className="text-xs text-muted-foreground">
                View uploaded docs
              </div>
            </div>
          </Button>

          <Button
            variant="outline"
            className="h-auto py-4 justify-start"
            onClick={() => navigate("/employee/profile")}
          >
            <ArrowRight className="mr-2 h-5 w-5" />
            <div className="text-left">
              <div className="font-medium">My Profile</div>
              <div className="text-xs text-muted-foreground">View Details</div>
            </div>
          </Button>
        </div>
      </div>

      {/* Recent Activity Summary */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Leave Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Leave Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-yellow-600" />
                <span className="text-sm">Pending</span>
              </div>
              <span className="font-bold">{dashboardData.leaves.pending}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm">Approved</span>
              </div>
              <span className="font-bold">{dashboardData.leaves.approved}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <XCircle className="h-4 w-4 text-red-600" />
                <span className="text-sm">Rejected</span>
              </div>
              <span className="font-bold">{dashboardData.leaves.rejected}</span>
            </div>
            <Button
              variant="link"
              className="w-full mt-2"
              onClick={() => navigate("/employee/leaves")}
            >
              View All Leaves <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>

        {/* Attendance Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              This Month's Attendance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {dashboardData.attendance.currentMonth ? (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    Working Days
                  </span>
                  <span className="font-bold">
                    {dashboardData.attendance.currentMonth.workingDays}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    Paid Leaves
                  </span>
                  <span className="font-bold">
                    {dashboardData.attendance.currentMonth.paidLeaves}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    Unpaid Leaves
                  </span>
                  <span className="font-bold">
                    {dashboardData.attendance.currentMonth.unpaidLeaves}
                  </span>
                </div>
                <div className="pt-2 border-t">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Attendance Rate</span>
                    <span className="text-lg font-bold text-green-600">
                      {attendancePercentage}%
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <AlertCircle className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">
                  No attendance recorded for this month yet
                </p>
              </div>
            )}
            <Button
              variant="link"
              className="w-full mt-2"
              onClick={() => navigate("/employee/attendance")}
            >
              View All Records <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
